import { scaleLinear, type ScaleBand } from 'd3-scale';
import { Bar } from '../Bar';
import { getElement } from '../../utils/non-empty-array';
import type { Accessor, ChoicesData, ChoicesStaticOptions } from '../../types';
import { MIN_BAR_HEIGHT } from '../constants';
import { useScoreValueAlternator } from './useScoreValueAlternator';

interface HiddenScoreBarsProps {
  data: ChoicesData[];
  staticOptions: ChoicesStaticOptions[];
  keyAccessor: Accessor<ChoicesStaticOptions, string>;
  xScale: ScaleBand<string>;
  colors: readonly [string, ...string[]];
  disableAnimations: boolean;
  boundedHeight: number;
  images: Record<string, string> | null;
}

export const HiddenScoreBars = ({
  data,
  staticOptions,
  keyAccessor,
  xScale,
  colors,
  disableAnimations,
  boundedHeight,
  images,
}: HiddenScoreBarsProps) => {
  const minHeight = 0;
  const maxHeight = 2;

  const linearScale = scaleLinear()
    .domain([0, 10])
    .range([MIN_BAR_HEIGHT, boundedHeight]);

  const scoreSum = data.reduce((sum, d) => sum + (d.score || 0), 0);

  const barsValue = useScoreValueAlternator({
    valueChangeTrigger: scoreSum,
    lowValue: minHeight,
    highValue: maxHeight,
  });

  return (
    <g data-name="bars">
      {staticOptions.map((d, i) => {
        const color = getElement(colors, i);
        const x = xScale(keyAccessor(d));
        const width = xScale.bandwidth();
        const y = boundedHeight - linearScale(barsValue);

        const backgroundImage = images?.[keyAccessor(d)];

        if (!x) {
          return null;
        }

        return (
          <g data-value={barsValue} key={d.id}>
            <Bar
              image={images ? { imageUrl: backgroundImage } : undefined}
              color={color}
              x={x}
              y={y}
              width={width}
              height={linearScale(barsValue)}
              disableAnimations={disableAnimations}
              opacity={0.4}
              animationDelay={i * 100}
            />
          </g>
        );
      })}
    </g>
  );
};
